import * as React from "react";

import { makeStyles } from "@material-ui/styles";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";

import { PageHeader, translate } from "jazasoft";

import MuiTable from "../../../components/MuiTable";
import Tabs from "../../../components/tab/Tabs";
import Tab from "../../../components/tab/SubTab";

import { useDeepEffect, useDeepMemo, useQuery, useSetting, useUnit } from "../../../hooks";

import SettingKeys from "../../../utils/SettingKeys";
import { Entity, OState } from "../../../utils/types";
import { getDistinctValues, isEmpty, isTrue, toFixed, toFixedSigned, toFixedValue } from "../../../utils/helpers";
import { getEffFlowInfoList } from "../../../helpers/orderHelper";
import Table from "../../../components/Table";

const patternValue = group => {
  if (!isEmpty(group?.patternNumber)) {
    return !isEmpty(group?.warpPattern) && !isEmpty(group?.weftPattern)
      ? `L${toFixedSigned(group.warpPattern, 1)}% W${toFixedSigned(group.weftPattern, 1)}% (${group.patternNumber})`
      : "-";
  } else {
    return !isEmpty(group?.warpPattern) && !isEmpty(group?.weftPattern)
      ? `L${toFixedSigned(group.warpPattern, 1)}% W${toFixedSigned(group.weftPattern, 1)}%`
      : "-";
  }
};

const useStyles = makeStyles({
  content: {
    margin: "1.5em",
    marginTop: 0
  },
  mrTop: {
    marginTop: "1.5em"
  },
  sidePadding: {
    padding: "0 1.5em"
  },
  bottomPadding: {
    paddingBottom: "1.5em"
  },
  panelDetail: { padding: 0, flexDirection: "column" }
});

const PartRecuttingView = props => {
  const classes = useStyles();
  const { id, translate } = props;

  const [selectedKey, setSelectedKey] = React.useState(null);
  const [tabIdx, setTabIdx] = React.useState(0);

  const rollFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ROLLS);
  const defectsVal = useSetting(SettingKeys.DEFECT_TYPES);
  const isTraceableVal = useSetting(SettingKeys.PANEL_REPLACEMENT_TRACEABLE);
  const isTraceable = isTrue(isTraceableVal);

  const defects = isEmpty(defectsVal) ? [] : JSON.parse(defectsVal);
  const { allUnitList } = useUnit();

  const { data: order = {} } = useQuery({ url: `orders/${id}`, params: { page: "PartRecutting" } });

  const { data: cutOrders = [] } = useQuery({ url: `cut-orders`, params: { orderId: id, sort: "id,desc", size: 1000 } });

  const flowInfoRows = useDeepMemo(() => {
    if (isEmpty(cutOrders)) return [];
    let coList = [];
    if (!isEmpty(order)) {
      coList = cutOrders.flatMap(cutOrder => {
        const flowInfoList = getEffFlowInfoList(order, cutOrder);
        const productName = cutOrder?.productIdList
          ?.map((id) => order.flowInfoList?.find((e) => e.product?.id === id)?.product?.name)
          .filter((e) => !isEmpty(e))
          .join(', ');
        return flowInfoList.map((flowInfo, idx) => ({
          ...flowInfo,
          cutOrder,
          key: `${cutOrder.id}-${idx}`,
          styles: flowInfo.style,
          colors: flowInfo.color,
          cutOrderId: cutOrder?.id,
          poRef: order.poRef,
          productName,
          effPartName: cutOrder.part,
          lotValue: cutOrder.lotValue,
          fpo: cutOrder.fpo,
          flowRefs: flowInfo.flowRef,
          customerFlowRefs: flowInfo.customerFlowRef,
          inseams: flowInfo.inseam,
          destinations: flowInfo.destination,
          deliveries: flowInfo.deliveryDate,
          state: cutOrder.state,
          unit: allUnitList?.find(u => u.unitId === cutOrder.unitId)?.name || cutOrder.unitId
        }));
      });
    }
    return coList.map(row => ({ ...row, poRef: order?.poRef, customerName: order?.customer?.name, seasonName: order?.season?.name }));
  }, [order, cutOrders, allUnitList]);

  const flowInfoColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "poRef", title: "PO Ref" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: "unit", title: "Unit", highlight: true },
      { dataKey: "fpo", title: "FPO", highlight: true },
      { dataKey: "flowRef", title: "Flow Ref.", render: value => value || "-", highlight: true },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", render: value => value || "-", highlight: true },
      { dataKey: "productName", title: "Product", filterOptions: { filter: true }, highlight: true },
      { dataKey: "style", title: "Style", filterOptions: { filter: true }, highlight: true },
      { dataKey: "color", title: "Style Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: "effPartName", title: "Part", highlight: true },
      { dataKey: "lotValue", title: "Lot", highlight: true },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "fpoQty", title: "FPO Qty" },
      // { dataKey: "extra", title: "Extra (%)", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      { dataKey: "inseam", title: "Inseam", filterOptions: { filter: true }, highlight: true },
      { dataKey: "destination", title: "Destination", filterOptions: { filter: true }, highlight: true },
      { dataKey: "deliveryDate", title: "Delivery Date", filterOptions: { filter: true }, highlight: true },
      { dataKey: "state", title: "Status", filterOptions: { filter: true }, length: 100 }
    ];

    if (flowInfoRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.inseam))) {
      cols = cols.filter(c => c.dataKey !== "inseam");
    }
    if (flowInfoRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (flowInfoRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (flowInfoRows.every(e => isEmpty(e.fpo))) {
      cols = cols.filter(c => c.dataKey !== "fpo");
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => c.dataKey !== "fpoQty");
    }

    return cols;
  }, [flowInfoRows, allUnitList]);

  useDeepEffect(() => {
    if (!isEmpty(flowInfoRows)) {
      setSelectedKey(flowInfoRows[0].key);
    }
  }, [flowInfoRows.length]);

  const flowInfo = flowInfoRows.find(fi => fi.key === selectedKey);
  const selectedCutOrder = flowInfo?.cutOrder;
  const { data: cutOrder = {} } = useQuery(!isEmpty(selectedCutOrder?.id) && { url: `cut-orders/${selectedCutOrder?.id}` });
  const style = flowInfo?.style;
  const color = flowInfo?.color;

  const { data: rollList = [] } = useQuery(
    !isEmpty(cutOrder) &&
    !isEmpty(style) &&
    !isEmpty(color) && {
      url: `rolls`,
      params: { search: `cutOrderFlow.id==${cutOrder?.id};style=='${style}';color=='${color}';(hasChild=='NULL',hasChild=='FALSE')`, size: 100000 }
    }
  );
  const { data: panelInfoList = [] } = useQuery(
    !isEmpty(cutOrder) &&
    !isEmpty(style) &&
    !isEmpty(color) && {
      url: "panel-info",
      method: "get",
      params: { search: `orderFlow.id==${cutOrder?.id};style=='${style}';color=='${color}'`, size: 10000 }
    }
  );

  const onRowClick = row => {
    if (isEmpty(row.parentKey)) {
      setSelectedKey(row.key);
    }
  };

  const parts = useDeepMemo(() => {
    if (isEmpty(order) || isEmpty(cutOrder)) return [];
    const flowInfoList = getEffFlowInfoList(order, cutOrder);
    if (isEmpty(flowInfoList)) return [];
    if (flowInfoList?.length === 1) {
      return flowInfoList[0].product?.replacementPanels?.split(",")?.map(panel => ({ panel, dataKey: `${flowInfoList[0].product?.id}__${panel}`, title: panel, productId: flowInfoList[0].product?.id })) || [];
    } else {
      return flowInfoList.flatMap(({ product }) => {
        const panels = product.replacementPanels?.split(',').filter((e) => !isEmpty(e)) || [];
        return panels
          .map((panel) => panel.trim())
          .map((panel) => ({ dataKey: `${product.id}__${panel}`, title: panel, productId: product?.id, product: product.name, panel }));
      });
    }
  }, [order, cutOrder]);

  const { columns, headerRows = [] } = useDeepMemo(() => {
    let columns = [];
    let headerRows = [];

    const productLength = getDistinctValues(parts.map(e => e.productId))?.length || 0;
    if (tabIdx === 0) {
      if (productLength === 1) {
        columns = [
          { dataKey: "defect", title: "Defect Type" },
          ...parts,
          { dataKey: "total", title: "Total" }
        ];
      } else {
        columns = [{ dataKey: "defect", title: "Defect Type", align: "center" },];

        let groupRow = {};
        let product;
        for (let part of parts) {
          let leftBorder = false;
          if (product !== part.product) {
            leftBorder = true;
            product = part.product;
            groupRow[part.dataKey] = part.product;
          }
          columns.push({
            ...part,
            leftBorder,
            style: leftBorder ? { borderLeftWidth: 1 } : {}
          });
        }
        columns.push({ dataKey: "total", title: "Total", leftBorder: true, align: "center" });
        columns = columns.map((c) => ({ ...c, align: c.align || 'center' }));

        headerRows = [groupRow, columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
      }

    } else if (tabIdx === 1) {
      columns = [
        { dataKey: "rollNo", title: "F. Roll No" },
        { dataKey: "supplierRollNo", title: "S. Roll No" },
        { dataKey: "rollType", title: "Roll Type" },
        { dataKey: "shade", title: "Shade" },
        { dataKey: "pattern", title: "Pattern" },
        { dataKey: "used", title: "Used", render: value => `${toFixedValue(value)} m` }
      ];
      if (!rollFields.includes("supplierRollNo")) {
        columns = columns.filter(c => c.dataKey !== "supplierRollNo");
      }
    } else {
      columns = [
        { dataKey: "garmentNo", title: "Garment No" },
        { dataKey: "size", title: "Size" },
        { dataKey: "product", title: "Product" },
        { dataKey: "panel", title: "Panel" },
        { dataKey: "defect", title: "Defect Type" },
        // { dataKey: 'originalRollNo', title: 'Original Roll No' },
        { dataKey: "shade", title: "Shade" },
        { dataKey: "pattern", title: "Pattern" },
        { dataKey: "usedRollNo", title: "Used F. Roll No" },
        { dataKey: "usedSupplierRollNo", title: "Used S. Roll No" },
        { dataKey: "used", title: "Used", render: value => `${toFixedValue(value)} m` }
      ];
      if (!rollFields.includes("supplierRollNo")) {
        columns = columns.filter(c => c.dataKey !== "usedSupplierRollNo");
      }
    }

    return { columns, headerRows };
  }, [tabIdx, parts, rollFields]);

  const rows = useDeepMemo(() => {
    let rowList = [];
    const rolls = isEmpty(rollList)
      ? []
      : rollList
        .filter(roll => roll.usedInPanel > 0)
        .map(roll => ({ ...roll, panelDetailList: isEmpty(roll.usedInPanelDetails) ? [] : JSON.parse(roll.usedInPanelDetails) }));

    if (tabIdx === 0) {
      rowList = defects.map((defect) => {
        const piList = panelInfoList.filter((pi) => defect?.value === pi.defect);
        const partObject = parts.reduce((acc, part) => {
          const panelList = piList.filter((pi) => pi.panel?.trim() === part.panel?.trim() && pi.productId === part.productId);
          return { ...acc, [part.dataKey]: isEmpty(panelList) ? '-' : panelList.reduce((acc, pi) => acc + pi.qty, 0) };
        }, {});
        return {
          defect: defect?.value,
          ...partObject,
          total: Object.values(partObject)
            .filter((v) => !isEmpty(v) && v !== "-")
            .reduce((acc, v) => acc + Number(v), 0),
        };
      });
      rowList = [
        ...rowList,
        {
          defect: "total",
          ...parts.reduce(
            (acc, part) => ({
              ...acc,
              [part.dataKey]: rowList.filter((row) => !isEmpty(row[part.dataKey]) && row[part.dataKey] !== "-").reduce((acc, row) => acc + row[part.dataKey], 0),
            }),
            {}
          ),
          total: rowList.reduce((acc, row) => acc + row['total'], 0),
        },
      ];
    } else if (tabIdx === 1) {
      rowList = rolls.map(roll => {
        return {
          ...roll,
          pattern: patternValue(cutOrder?.groupList?.find(g => g.id === roll.finalGroupId)),
          used: roll.usedInPanel
        };
      });
      const totalUsed = rowList.reduce((acc, row) => acc + row.used || 0, 0);
      const totalRow = { rollNo: "Total", used: Number(toFixed(totalUsed, 2)) };

      rowList.push(totalRow);
    } else {
      const serialNoList = getDistinctValues(panelInfoList.map(panel => panel.serialNo));
      rowList = serialNoList.map(serialNo => {
        const panelList = panelInfoList.filter(panel => `${panel.serialNo}` === `${serialNo}`);

        const panels = getDistinctValues(panelList.map(e => e.panel)).join(", ");
        const productName = getDistinctValues(panelList.map(e => e?.productName)).join(", ");
        const defects = getDistinctValues(panelList.map(e => e.defect)).join(", ");

        const usedRolls = rolls.filter(roll => roll.panelDetailList.map(e => `${e.serialNo}`).includes(`${serialNo}`));

        const rollList = usedRolls.map(roll => ({
          ...roll,
          usedInPanel: roll.panelDetailList.find(e => `${e.serialNo}` === `${serialNo}`)?.usedInPanel || 0,
          pattern: patternValue(cutOrder?.groupList?.find(g => g.id === roll.finalGroupId))
        }));
        const usedInPanel = rollList.reduce((acc, e) => acc + e.usedInPanel, 0);
        const usedRollNumbers = getDistinctValues(rollList.map(e => e.rollNo)).join(", ");
        const usedSupplierRollNumbers = getDistinctValues(rollList.map(e => e.supplierRollNo)).join(", ");
        const shade = getDistinctValues(rollList.map(e => e.shade)).join(", ");
        const pattern = getDistinctValues(rollList.map(e => e.pattern))?.length <= 1 ? rollList.map(e => e.pattern)[0] : "Multiple";

        const panel = panelList[0];
        return {
          garmentNo: serialNo,
          size: !isEmpty(panel?.size) ? `${panel.size}${!isEmpty(panel?.sizeGroup) && panel.sizeGroup !== 'G1' ? ` (${panel.sizeGroup})` : ''}` : '',
          product: productName,
          panel: panels,
          defect: defects,
          shade,
          pattern,
          usedRollNo: usedRollNumbers,
          usedSupplierRollNo: usedSupplierRollNumbers,
          used: usedInPanel,
          panelList,
          rollList
        };
      });
      const totalUsed = rowList.reduce((acc, row) => acc + row.used || 0, 0);
      const totalRow = { garmentNo: "Total", used: Number(toFixed(totalUsed, 2)) };

      rowList.push(totalRow);
    }
    return rowList.map((row, idx) => ({ ...row, key: idx + 1 }));
  }, [tabIdx, panelInfoList, defects, rollList, cutOrder]);

  return (
    <div>
      <PageHeader title="Panel Replacement" />

      <div className={classes.content}>
        <MuiTable
          title={translate("resources.rollPlan.title.cut_order", { _: "Cut Order" })}
          toolbar={true}
          searchable={true}
          pageable={true}
          searchKeys={["flowRef", "customerFlowRef", "style", "styleNo"]}
          idKey="key"
          parentIdKey="parentKey"
          columns={flowInfoColumns}
          rows={flowInfoRows}
          defaultExpanded={false}
          expandedColor={["#BFDDF8", "#7AB9F0"]}
          onRowClick={onRowClick}
          rowStyle={({ row }) => ({ backgroundColor: selectedKey === row.key ? "#BFDDF8" : "none" })}
          i18nKey="resources.grouping.fields.flowInfo"
        />

        {cutOrder?.stateValue < OState.MAIN_FEEDBACK_IN_PROGRESS.stateValue && (
          <Alert variant="filled" severity="warning" className={classes.mrTop}>
            <Typography variant="h6">Laying not started yet. Recutting can be done after laying is started</Typography>
          </Alert>
        )}

        {cutOrder?.stateValue > OState.MAIN_FEEDBACK_IN_PROGRESS.stateValue && (
          <>
            <Tabs style={{ marginTop: "1.5em" }} value={tabIdx} onChange={(e, idx) => setTabIdx(idx)}>
              <Tab label={translate("resources.partRecutting.tabs.panel_summary", { _: "Panel Summary" })} />
              <Tab label={translate("resources.partRecutting.tabs.fabric_usage", { _: "Fabric Usage" })} />
              {isTraceable && <Tab label={translate("resources.partRecutting.tabs.traceability", { _: "Traceability" })} />}
            </Tabs>

            <div style={{ marginTop: "1.5em" }}>
              <Table
                headerRows={headerRows}
                columns={columns}
                rows={rows}
                idKey="key"
                parentIdKey="parentKey"
                headerCellStyle={column => ({
                  borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                  borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                  backgroundColor: "#C6D0DA"
                })}
                cellStyle={({ column, record }) =>
                  record.name === "Total"
                    ? {
                      fontWeight: "bold",
                      borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                      borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
                    }
                    : {
                      borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                      borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
                    }
                }
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default translate(PartRecuttingView);
